import React, { Component } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import axios from 'axios';

import Header from '../layout/header';
import Footer from '../layout/footer';
import AvailableCitiesModal from '../elements/available-cities-modal';
import './complaint-page.scss';

const API = 'https://www.ridenrepair.com/api/complaints';

const CATEGORIES = [
  ['SERVICE_QUALITY', 'Service not done properly'],
  ['MECHANIC_BEHAVIOUR', 'Mechanic behaviour'],
  ['OVERCHARGED', 'Overcharged / billing issue'],
  ['REFUND_PAYMENT', 'Refund / payment issue'],
  ['LATE_NO_SHOW', 'Mechanic late / did not come'],
  ['SPARE_PART', 'Spare part issue'],
  ['VEHICLE_DAMAGE', 'Vehicle damaged'],
  ['OTHER', 'Other'],
];
const MAX_FILES = 3;
const MAX_FILE_BYTES = 5 * 1024 * 1024;
const MAX_TOTAL_BYTES = 9 * 1024 * 1024;
const FILE_TYPES = 'image/jpeg,image/png,image/webp,application/pdf';
const EXT_MIME = { jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', webp: 'image/webp', pdf: 'application/pdf' };

const fileMime = (f) => {
  if (/^(image\/(jpeg|png|webp)|application\/pdf)$/.test(f.type || '')) return f.type;
  if (f.type) return null;
  const ext = String(f.name || '').split('.').pop().toLowerCase();
  return EXT_MIME[ext] || null;
};
const fmtSize = (n) => (n >= 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

// Copy the picked file into memory now: Android can fail later uploads of Drive/Photos picks (ERR_UPLOAD_FILE_CHANGED).
const readIntoMemory = (file, type) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(new File([r.result], file.name, { type }));
  r.onerror = () => reject(r.error);
  r.readAsArrayBuffer(file);
});

const errMsg = (e, fallback) => (e && e.response && e.response.data && e.response.data.error && e.response.data.error.message) || fallback;

// Downscale photos (≤1600px JPEG) so 3 phone photos fit the upload limit.
const compressImage = (file) => new Promise((resolve) => {
  if (!file.type || !/^image\/(jpeg|png|webp)$/.test(file.type)) { resolve(file); return; }
  const reader = new FileReader();
  reader.onload = (ev) => {
    const img = new Image();
    img.onload = () => {
      const MAX = 1600;
      let { width, height } = img;
      if (width > MAX || height > MAX) {
        const k = MAX / Math.max(width, height);
        width = Math.round(width * k);
        height = Math.round(height * k);
      }
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, width, height);
      ctx.drawImage(img, 0, 0, width, height);
      canvas.toBlob((blob) => {
        if (!blob || blob.size >= file.size) { resolve(file); return; }
        resolve(new File([blob], file.name.replace(/\.\w+$/, '') + '.jpg', { type: 'image/jpeg' }));
      }, 'image/jpeg', 0.82);
    };
    img.onerror = () => resolve(file);
    img.src = ev.target.result;
  };
  reader.onerror = () => resolve(file);
  reader.readAsDataURL(file);
});

class Complaint extends Component {
  state = {
    booking_id: '',
    phone: '',
    name: '',
    category: '',
    description: '',
    website: '',          // honeypot (hidden)
    files: [],            // [{ id, file, name, size, isPdf, url }]
    fileError: '',
    reading: false,
    submitting: false,
    error: '',
    result: null,         // { ref, existing, status }
    trackRef: '',
    trackPhone: '',
    tracking: false,
    trackError: '',
    trackResult: null,
  };

  componentDidMount() {
    // Pre-fill from the booking page (router state, never the URL).
    const st = (this.props.location && this.props.location.state) || {};
    const next = {};
    if (st.bookingId) next.booking_id = String(st.bookingId);
    if (st.phone) next.phone = String(st.phone).replace(/\D/g, '').slice(-10);
    if (st.name) next.name = String(st.name);
    if (!next.name && this.props.user && this.props.user.name) next.name = this.props.user.name;
    if (Object.keys(next).length) this.setState(next);
  }

  componentWillUnmount() {
    this.revokeAll(this.state.files);
  }

  set = (k) => (e) => this.setState({ [k]: e.target.value, error: '' });

  revokeAll = (files) => files.forEach((f) => { if (f.url) URL.revokeObjectURL(f.url); });

  onFiles = async (e) => {
    const input = e.target;
    const picked = Array.from(input.files || []);
    input.value = '';   // lets the same file be picked again after removing it
    if (!picked.length) return;
    this.setState({ reading: true, fileError: '' });
    const files = this.state.files.slice();
    let fileError = '';
    for (const f of picked) {
      if (files.length >= MAX_FILES) { fileError = `You can add up to ${MAX_FILES} files.`; break; }
      const type = fileMime(f);
      if (!type) {
        fileError = /\.hei[cf]$/i.test(f.name || '') || /hei[cf]/i.test(f.type || '')
          ? 'HEIC photos are not supported. Please choose a JPG, or take a screenshot of the photo.'
          : `"${f.name}" is not a photo or PDF. Please use JPG, PNG, WEBP or PDF.`;
        continue;
      }
      if (type === 'application/pdf' && f.size > MAX_FILE_BYTES) { fileError = `"${f.name}" is larger than 5 MB.`; continue; }
      if (files.some((x) => x.name === f.name && x.size === f.size)) continue;
      let mem;
      try { mem = await readIntoMemory(f, type); } catch (ex) { fileError = `Couldn't read "${f.name}". Please pick it again.`; continue; }
      const isPdf = type === 'application/pdf';
      files.push({ id: `${Date.now()}-${files.length}-${f.size}`, file: mem, name: f.name, size: f.size, isPdf, url: isPdf ? null : URL.createObjectURL(mem) });
    }
    this.setState({ files, fileError, reading: false });
  };

  removeFile = (id) => {
    const gone = this.state.files.filter((f) => f.id === id);
    this.revokeAll(gone);
    this.setState({ files: this.state.files.filter((f) => f.id !== id), fileError: '' });
  };

  submit = async (e) => {
    e.preventDefault();
    const { booking_id, phone, name, category, description, website, files } = this.state;
    const bid = booking_id.trim().replace(/^#/, '');
    const ph = phone.replace(/\D/g, '').slice(-10);
    if (!/^\d{1,12}$/.test(bid)) { this.setState({ error: 'Enter your booking ID (numbers only). You can find it in your booking SMS / WhatsApp.' }); return; }
    if (ph.length !== 10) { this.setState({ error: 'Enter the 10-digit phone number used for the booking.' }); return; }
    if (name.trim().length < 2) { this.setState({ error: 'Enter your name.' }); return; }
    if (!category) { this.setState({ error: 'Choose what your complaint is about.' }); return; }
    if (description.trim().length < 10) { this.setState({ error: 'Please tell us what happened (at least 10 characters).' }); return; }
    if (this.state.reading) return;
    this.setState({ submitting: true, error: '' });
    try {
      const ready = await Promise.all(files.map((f) => compressImage(f.file)));
      const big = ready.findIndex((f) => f.size > MAX_FILE_BYTES);
      if (big >= 0) { this.setState({ submitting: false, error: `"${files[big].name}" is larger than 5 MB. Please remove it or choose a smaller file.` }); return; }
      if (ready.reduce((n, f) => n + f.size, 0) > MAX_TOTAL_BYTES) { this.setState({ submitting: false, error: 'Files are too large together (max 9 MB). Please remove one.' }); return; }
      const fd = new FormData();
      fd.append('booking_id', bid);
      fd.append('phone', ph);
      fd.append('name', name.trim());
      fd.append('category', category);
      fd.append('description', description.trim());
      fd.append('brand', 'RNR');
      if (website) fd.append('website', website);
      ready.forEach((f) => fd.append('photos', f));
      const res = await axios.post(`${API}/public`, fd);
      this.revokeAll(files);
      this.setState({ submitting: false, result: res.data, trackRef: res.data.ref, trackPhone: ph, files: [], fileError: '' });
      if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (ex) {
      this.setState({ submitting: false, error: errMsg(ex, 'Something went wrong. Please try again, or call us.') });
    }
  };

  track = async (e) => {
    e.preventDefault();
    const { trackRef, trackPhone } = this.state;
    if (!trackRef.trim() || trackPhone.replace(/\D/g, '').length < 10) {
      this.setState({ trackError: 'Enter your complaint number and phone number.' });
      return;
    }
    this.setState({ tracking: true, trackError: '', trackResult: null });
    try {
      const res = await axios.post(`${API}/public/status`, { ref: trackRef.trim(), phone: trackPhone.replace(/\D/g, '').slice(-10) });
      this.setState({ tracking: false, trackResult: res.data });
    } catch (ex) {
      this.setState({ tracking: false, trackError: errMsg(ex, 'Could not check the status. Please try again.') });
    }
  };

  render() {
    const s = this.state;
    return (
      <>
        <Helmet>
          <title>Raise a Complaint | Ride N Repair</title>
          <meta name="description" content="Not happy with your Ride N Repair service? Raise a complaint with your booking ID and our team will contact you." />
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        {this.props.citiesModal && <AvailableCitiesModal closeModal={() => this.props.setCitiesModal(false)} />}
        <Header user={this.props.user} setUser={this.props.setUser} isMobileApp={this.props.isMobileApp} />

        <div className="page-content bg-white">
          <section className="cmp-page">
            <div className="container">
              <div className="cmp-head">
                <h1>Raise a complaint</h1>
                <p>Sorry something went wrong. Tell us about it — our team will call you back. Complaints are usually answered within a few working hours.</p>
              </div>

              <div className="cmp-grid">
                <div className="cmp-card">
                  {s.result ? (
                    <div className="cmp-done">
                      <div className="cmp-done-icon">✓</div>
                      <h2>{s.result.existing ? 'Added to your existing complaint' : 'Complaint received'}</h2>
                      <p>Your complaint number is</p>
                      <div className="cmp-ref">{s.result.ref}</div>
                      {s.result.files > 0 && <p className="cmp-attached">📎 {s.result.files} {s.result.files === 1 ? 'file' : 'files'} attached</p>}
                      <p className="cmp-muted">Please save this number. Our team will contact you on the phone number you gave.</p>
                      <p className="cmp-muted cmp-small">Forgot a photo or bill? While this complaint is open, submit the form again with the same booking ID and a short note like "adding the bill" — it is added to this complaint.</p>
                      <button type="button" className="cmp-btn cmp-btn--ghost" onClick={() => { this.revokeAll(s.files); this.setState({ result: null, description: '', category: '', files: [], fileError: '' }); }}>
                        Raise another complaint
                      </button>
                    </div>
                  ) : (
                    <form onSubmit={this.submit} noValidate>
                      <div className="cmp-row2">
                        <label className="cmp-field">
                          <span>Booking ID *</span>
                          <input value={s.booking_id} onChange={this.set('booking_id')} inputMode="numeric" placeholder="e.g. 209002" />
                        </label>
                        <label className="cmp-field">
                          <span>Phone number used for booking *</span>
                          <input value={s.phone} onChange={this.set('phone')} inputMode="tel" placeholder="10-digit mobile number" maxLength={14} />
                        </label>
                      </div>
                      <label className="cmp-field">
                        <span>Your name *</span>
                        <input value={s.name} onChange={this.set('name')} maxLength={100} />
                      </label>
                      <label className="cmp-field">
                        <span>What is your complaint about? *</span>
                        <select value={s.category} onChange={this.set('category')}>
                          <option value="">Choose…</option>
                          {CATEGORIES.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                        </select>
                      </label>
                      <label className="cmp-field">
                        <span>What happened? *</span>
                        <textarea rows={5} value={s.description} onChange={this.set('description')} maxLength={2000}
                          placeholder="Tell us what went wrong, and what you would like us to do." />
                      </label>
                      <div className="cmp-field">
                        <span>Photos or PDF (optional)</span>
                        <small className="cmp-hint">Bill, receipt, damage photo or screenshot · up to {MAX_FILES} · max 5 MB each</small>
                        {s.files.length > 0 && (
                          <div className="cmp-files">
                            {s.files.map((f) => (
                              <div className="cmp-file" key={f.id}>
                                {f.isPdf ? <div className="cmp-file-pdf">PDF</div> : <img src={f.url} alt="" />}
                                <div className="cmp-file-meta">
                                  <span className="cmp-file-name">{f.name}</span>
                                  <span className="cmp-muted">{fmtSize(f.size)}</span>
                                </div>
                                <button type="button" className="cmp-file-x" aria-label={`Remove ${f.name}`} onClick={() => this.removeFile(f.id)}>✕</button>
                              </div>
                            ))}
                          </div>
                        )}
                        {s.files.length < MAX_FILES && (
                          <label className={`cmp-add${s.reading ? ' is-busy' : ''}`}>
                            📎 {s.reading ? 'Adding…' : (s.files.length ? 'Add more' : 'Add photos / PDF')}
                            <input type="file" multiple accept={FILE_TYPES} onChange={this.onFiles} disabled={s.reading} />
                          </label>
                        )}
                        {s.fileError && <div className="cmp-error cmp-error--inline">{s.fileError}</div>}
                      </div>
                      <div className="cmp-hp" aria-hidden="true">
                        <label>Website<input tabIndex={-1} autoComplete="off" value={s.website} onChange={this.set('website')} /></label>
                      </div>
                      {s.error && <div className="cmp-error">{s.error}</div>}
                      <button type="submit" className="cmp-btn" disabled={s.submitting || s.reading}>{s.submitting ? 'Sending…' : 'Submit complaint'}</button>
                      <p className="cmp-muted cmp-small">Don't know your booking ID? Check your booking SMS / WhatsApp, or see <Link to="/booking-history">your bookings</Link>.</p>
                    </form>
                  )}
                </div>

                <div className="cmp-card cmp-card--side">
                  <h2>Track your complaint</h2>
                  <form onSubmit={this.track} noValidate>
                    <label className="cmp-field">
                      <span>Complaint number</span>
                      <input value={s.trackRef} onChange={(e) => this.setState({ trackRef: e.target.value, trackError: '' })} placeholder="CMP-000123" />
                    </label>
                    <label className="cmp-field">
                      <span>Phone number</span>
                      <input value={s.trackPhone} onChange={(e) => this.setState({ trackPhone: e.target.value, trackError: '' })} inputMode="tel" maxLength={14} />
                    </label>
                    {s.trackError && <div className="cmp-error">{s.trackError}</div>}
                    <button type="submit" className="cmp-btn cmp-btn--ghost" disabled={s.tracking}>{s.tracking ? 'Checking…' : 'Check status'}</button>
                  </form>
                  {s.trackResult && (
                    <div className="cmp-status">
                      <div className="cmp-muted">{s.trackResult.ref}</div>
                      <b>{s.trackResult.status_label}</b>
                      <div className="cmp-muted cmp-small">Last update: {new Date(s.trackResult.updated_at).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' })}</div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </section>
        </div>

        <Footer />
      </>
    );
  }
}

export default Complaint;
